import { Alert } from "@mui/material";
import { FormEvent, useState } from "react";
import DefaultButton from "../../../../components/buttons/default-button";
import { FormControlCheckbox } from "../../../../components/form-control-checkbox";
import { TextField } from "../../../../components/text-field";
import DefaultText from "../../../../components/typography/default-text";
import { cellphoneMask, cpfMask, rgMask } from "../../../../utils/masks";
import { FormaInformacaoEnum } from "./interfaces/FormaInformacaoEnum";
import { sendFormularioLgpdEmail } from "./services/sendFormularioLgpdEmail";
import styles from "./styles.module.css";

type SubmitFeedback = {
  type: "success" | "error";
  message: string;
};

const FormularioLGPD: React.FC = () => {
  const [nome, setNome] = useState<string>("");
  const [cpf, setCpf] = useState<string>("");
  const [rg, setRg] = useState<string>("");
  const [telefone, setTelefone] = useState<string>("");
  const [quemSolicita, setQuemSolicita] = useState<string>("");
  const [dadosSolicitados, setDadosSolicitados] = useState<string>("");
  const [formaInformacao, setFormaInformacao] = useState<FormaInformacaoEnum>();
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [outroSolicitanteValue, setOutroSolicitanteValue] =
    useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitFeedback, setSubmitFeedback] = useState<SubmitFeedback | null>(
    null,
  );

  const isValid: boolean =
    acceptedTerms &&
    nome !== "" &&
    cpf !== "" &&
    rg !== "" &&
    telefone !== "" &&
    quemSolicita !== "" &&
    dadosSolicitados !== "" &&
    formaInformacao !== undefined &&
    (quemSolicita === "Outro" ? outroSolicitanteValue !== "" : true);

  const resetForm = () => {
    setNome("");
    setCpf("");
    setRg("");
    setTelefone("");
    setQuemSolicita("");
    setDadosSolicitados("");
    setFormaInformacao(undefined);
    setAcceptedTerms(false);
    setOutroSolicitanteValue("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isValid || !formaInformacao || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitFeedback(null);

    try {
      await sendFormularioLgpdEmail({
        nome,
        cpf,
        rg,
        telefone,
        quemSolicita,
        outroSolicitante:
          quemSolicita === "Outro" ? outroSolicitanteValue : undefined,
        dadosSolicitados,
        formaInformacao,
      });

      setSubmitFeedback({
        type: "success",
        message:
          "Solicitação enviada com sucesso. Em breve entraremos em contato.",
      });
      resetForm();
    } catch {
      setSubmitFeedback({
        type: "error",
        message:
          "Não foi possível enviar sua solicitação. Tente novamente em instantes.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles["sub-page-content"]}>
        <TextField
          fullWidth
          label="Seu nome *"
          placeholder="Seu nome aqui"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <TextField
          fullWidth
          label="CPF *"
          placeholder="CPF"
          value={cpf}
          onChange={(e) => {
            const maskedValue = cpfMask(e.target.value);
            setCpf(maskedValue);
          }}
        />
        <TextField
          fullWidth
          label="RG *"
          placeholder="RG"
          value={rg}
          onChange={(e) => {
            const maskedValue = rgMask(e.target.value);
            setRg(maskedValue);
          }}
        />
        <TextField
          fullWidth
          label="Telefone DDD *"
          placeholder="(12) 12345-6789"
          value={telefone}
          onChange={(e) => {
            const maskedValue = cellphoneMask(e.target.value);
            setTelefone(maskedValue);
          }}
        />
        <div className={styles["form-control-container"]}>
          <DefaultText
            lineHeight="16px"
            sx={{ color: "black", fontSize: "16px", fontWeight: "600" }}
          >
            Quem solicita é *
          </DefaultText>
          <FormControlCheckbox
            label="Cliente"
            checked={quemSolicita === "Cliente"}
            onChange={() => setQuemSolicita("Cliente")}
          />
          <FormControlCheckbox
            label="Colaborador"
            checked={quemSolicita === "Colaborador"}
            onChange={() => setQuemSolicita("Colaborador")}
          />
          <FormControlCheckbox
            label="Outro"
            checked={quemSolicita === "Outro"}
            onChange={() => setQuemSolicita("Outro")}
          />
          <TextField
            fullWidth
            placeholder="Outro digite"
            value={quemSolicita === "Outro" ? outroSolicitanteValue : ""}
            onChange={(e) => setOutroSolicitanteValue(e.target.value)}
            disabled={quemSolicita !== "Outro"}
          />
        </div>
        <TextField
          fullWidth
          label="Dados solicitados *"
          value={dadosSolicitados}
          multiline
          largeText
          onChange={(e) => setDadosSolicitados(e.target.value)}
        />
        <div className={styles["form-control-container"]}>
          <DefaultText
            lineHeight="16px"
            sx={{ color: "black", fontSize: "16px", fontWeight: "600" }}
          >
            Em razão da Lei Geral de Proteção de Dados Pessoais (LGPD) o
            requerente solicita que os dados acima sejam informados da seguinte
            forma: *
          </DefaultText>
          <FormControlCheckbox
            label="Confirmação de existência de tratamento"
            checked={formaInformacao === FormaInformacaoEnum.CONFIRMACAO}
            onChange={() => setFormaInformacao(FormaInformacaoEnum.CONFIRMACAO)}
          />
          <FormControlCheckbox
            label="Acesso completo aos dados"
            checked={formaInformacao === FormaInformacaoEnum.ACESSO_COMPLETO}
            onChange={() =>
              setFormaInformacao(FormaInformacaoEnum.ACESSO_COMPLETO)
            }
          />
          <FormControlCheckbox
            label="Correção ou atualização dos dados"
            checked={formaInformacao === FormaInformacaoEnum.CORRECAO}
            onChange={() => setFormaInformacao(FormaInformacaoEnum.CORRECAO)}
          />
          <FormControlCheckbox
            label="Anonimização, bloqueio ou eliminação de dados tratados em desconformidade com a lei"
            checked={formaInformacao === FormaInformacaoEnum.ANONIMIZACAO}
            onChange={() =>
              setFormaInformacao(FormaInformacaoEnum.ANONIMIZACAO)
            }
          />
        </div>
        <div className={styles["form-control-container"]}>
          <DefaultText
            lineHeight="16px"
            sx={{ color: "black", fontSize: "16px", fontWeight: "600" }}
          >
            Ao marcar esta caixa, você estará de acordo com nosso “Termo de
            Consetimento para Tratamento de Dados” *
          </DefaultText>
          <FormControlCheckbox
            label="Estou de acordo com os Termos de Consentimento para Tratamento de Dados"
            checked={acceptedTerms}
            onChange={() => setAcceptedTerms((prev) => !prev)}
          />
        </div>
        {submitFeedback && (
          <Alert severity={submitFeedback.type}>{submitFeedback.message}</Alert>
        )}
        <DefaultButton
          variant="contained"
          type="submit"
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? "Enviando..." : "Enviar"}
        </DefaultButton>
      </div>
    </form>
  );
};

export default FormularioLGPD;
